(() => {
  'use strict';
  if (window.swOrderMhd) return;
  const KEY = '_MHD';
  const root = (window.Shopify && window.Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);
  let queue = Promise.resolve();
  let cartWrites = Promise.resolve();
  const pending = new Set();
  let resuming = false;
  const status = { ready: false, lastError: null };
  window.swOrderMhd = status;

  function properties(value) {
    if (Array.isArray(value)) return Object.fromEntries(value);
    return { ...(value || {}) };
  }
  function signature(value) {
    const p = properties(value);
    delete p[KEY];
    return JSON.stringify(Object.keys(p).sort().map(k => [k, p[k]]));
  }
  function sectionNames(body) {
    try {
      let s;
      if (body instanceof FormData || body instanceof URLSearchParams) s = body.get('sections');
      else if (typeof body === 'string') {
        try { s = JSON.parse(body).sections; }
        catch (_) { s = new URLSearchParams(body).get('sections'); }
      }
      return (Array.isArray(s) ? s : String(s || '').split(',')).filter(Boolean).slice(0, 5);
    } catch (_) { return []; }
  }
  async function synchronize(sections = []) {
    let changed = false;
    let result = null;
    // Refresh after each write: changing properties changes line keys and can merge lines.
    for (let attempt = 0; attempt < 100; attempt += 1) {
      const url = new URL(root + 'cart', location.origin);
      url.searchParams.set('view', 'sw-mhd');
      url.searchParams.set('_', String(Date.now()));
      const response = await nativeFetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('MHD lookup failed: ' + response.status);
      const metadata = await response.json();
      if (!Array.isArray(metadata.items)) throw new Error('MHD lookup returned no cart data');
      const line = metadata.items.find(item => {
        const p = properties(item.properties);
        return item.mhd ? p[KEY] !== item.mhd : Boolean(p[KEY]);
      });
      if (!line) {
        status.ready = true;
        status.lastError = null;
        return { changed, cart: result };
      }
      const p = properties(line.properties);
      if (line.mhd) p[KEY] = line.mhd;
      else p[KEY] = '';
      const payload = { id: line.key, quantity: line.quantity, properties: p };
      if (sections.length) {
        payload.sections = sections;
        payload.sections_url = location.pathname;
      }
      const write = await nativeFetch(root + 'cart/change.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!write.ok) throw new Error('MHD snapshot failed: ' + write.status);
      result = await write.json();
      changed = true;
    }
    throw new Error('MHD snapshot exceeded cart limit');
  }
  function schedule(sections = []) {
    status.ready = false;
    const work = queue.catch(() => {}).then(() => synchronize(sections));
    queue = work;
    return work;
  }
  function report(error) {
    status.ready = false;
    status.lastError = String(error.message || error);
    console.warn('[Schoko-Wunderland MHD]', status.lastError);
  }
  function patchAddedItem(item, cart) {
    const matches = cart.items.filter(candidate =>
      String(candidate.variant_id) === String(item.variant_id || item.id) &&
      signature(candidate.properties) === signature(item.properties));
    if (matches.length !== 1) return item;
    return { ...item, key: matches[0].key, properties: matches[0].properties, quantity: matches[0].quantity };
  }
  window.fetch = function swMhdFetch(input, init) {
    let url;
    try { url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.origin); }
    catch (_) { return nativeFetch(input, init); }
    const method = (init && init.method) || (input && input.method) || 'GET';
    if (url.origin !== location.origin || !/^POST$/i.test(method) || !/\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname)) {
      return nativeFetch(input, init);
    }
    status.ready = false;
    const work = cartWrites.catch(() => {}).then(async () => {
      const response = await nativeFetch(input, init);
      if (!response.ok) return response;
      try {
        const names = sectionNames(init && init.body);
        const snapshot = await schedule(names);
        if (!snapshot.changed || !snapshot.cart) return response;
        const original = await response.clone().json();
        let updated;
        if (/\/cart\/add/.test(url.pathname)) {
          updated = Array.isArray(original.items)
            ? { ...original, items: original.items.map(item => patchAddedItem(item, snapshot.cart)) }
            : patchAddedItem(original, snapshot.cart);
          if (snapshot.cart.sections) updated.sections = snapshot.cart.sections;
        } else {
          updated = { ...original, ...snapshot.cart };
        }
        const headers = new Headers(response.headers);
        headers.delete('content-length');
        headers.delete('content-encoding');
        headers.set('content-type', 'application/json');
        return new Response(JSON.stringify(updated), { status: response.status, statusText: response.statusText, headers });
      } catch (error) {
        // Best-before metadata must never stop a customer from buying.
        report(error);
        return response;
      }
    });
    cartWrites = work;
    pending.add(work);
    work.finally(() => pending.delete(work)).catch(() => {});
    return work;
  };

  function updateForm(form) {
    const field = form.querySelector('[data-sw-mhd-field]');
    const data = form.querySelector('[data-sw-mhd-variants]');
    const id = form.querySelector('[name="id"]');
    if (!field || !data || !id) return;
    try {
      const value = JSON.parse(data.textContent)[id.value] || '';
      if (field.value !== value) field.value = value;
      if (field.disabled !== !value) field.disabled = !value;
    } catch (error) { report(error); }
  }
  function updateForms() {
    document.querySelectorAll('form').forEach(form => {
      if (form.querySelector('[data-sw-mhd-field]')) updateForm(form);
    });
  }
  document.addEventListener('change', updateForms, true);
  document.addEventListener('submit', event => { updateForm(event.target); }, true);
  document.addEventListener('click', event => {
    const form = event.target.closest && event.target.closest('form');
    if (form) updateForm(form);
  }, true);
  new MutationObserver(updateForms).observe(document.documentElement, {
    subtree: true, childList: true, attributes: true, attributeFilter: ['value']
  });
  updateForms();

  async function beforeCheckout(resume) {
    try {
      await Promise.allSettled(Array.from(pending));
      await schedule();
    } catch (error) { report(error); }
    resuming = true;
    try { resume(); } finally { resuming = false; }
  }
  document.addEventListener('click', event => {
    if (resuming || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target.closest && event.target.closest('a[href],button[name="checkout"],input[name="checkout"],#vitals-cart_drawer-container .checkout-button');
    if (!target) return;
    const isCheckoutLink = target.matches('a[href]') && /\/checkout\/?$/.test(new URL(target.href, location.origin).pathname);
    const isDrawerCheckout = target.matches('#vitals-cart_drawer-container .checkout-button');
    if (!isCheckoutLink && !isDrawerCheckout && target.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    beforeCheckout(() => target.click());
  }, true);
  document.addEventListener('submit', event => {
    if (resuming || !event.submitter || event.submitter.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const form = event.target;
    const submitter = event.submitter;
    beforeCheckout(() => form.requestSubmit(submitter));
  }, true);
})();
